// Authored HTML is not trusted HTML.
//
// A `richtext` attribute is rendered with `dangerouslySetInnerHTML`, and since
// the authoring milestone its value can come from any registered account:
// `AttrInput` invites the author to write HTML, an admin reviews that HTML in
// their own session, and a published system puts it in front of every
// anonymous visitor. Unsanitised, that is stored XSS with an admin-session
// escalation — verified in a real browser before this file existed
// (`e2e/authored-html-xss.spec.js`), where an authored `<p onmouseover>` and an
// `<iframe srcdoc>` both executed on the deployed origin.
//
// ── Why sanitising happens HERE, at render, and not at write ───────────────
//
// Rendering is the only choke point that sees every value. Content reaches the
// `entry.attrs` jsonb by at least five paths — the authoring entry route, the
// Family document route, system import, layer patches, and `mrcs:load` from
// `db/mrcs/` — so a filter on the authoring route would have left import and
// the loader open, and would have protected none of the 23,528 richtext values
// already in the database. Sanitising at write would also rewrite base content
// in place, which `mrcs:export`'s byte-identity round trip forbids outright.
//
// Write-time validation still has a place, but as *feedback to the author*
// rather than as the security boundary: telling someone their `<script>` was
// dropped is a better editor, and this file is what makes it true either way.
//
// ── Why this is safe for the base corpus ───────────────────────────────────
//
// The whole of `db/mrcs/` was run through this configuration before it shipped:
// 23,528 richtext values, of which 6,102 changed as MARKUP (`<br />` → `<br>`,
// an implied `<tbody>` — exactly the normalisations the browser's own parser
// performs when it assigns innerHTML, so the resulting DOM is unchanged) and
// **zero** changed in rendered text. Two attributes were dropped corpus-wide, a
// malformed `class-` and an `xml:lang`, neither of which renders. That zero is
// what makes the change safe for the DOM parity gate, which diffs innerText.
//
// ── Why an allowlist library and not a denylist ────────────────────────────
//
// A list of forbidden tags is not a fix: it cannot see `<p onmouseover>`, it
// cannot see `javascript:` in an `href`, and it cannot see the mutation-XSS
// shapes where the parser relocates content out of a context that cannot hold
// it. DOMPurify parses to a DOM and keeps only what is on its allowlist, which
// is the only approach that answers all three.
import DOMPurify from 'dompurify';
import { isUploadAddress } from './uploadPaths';

// `USE_PROFILES: { html: true }` is an allowlist, and it also switches OFF the
// SVG and MathML profiles: nothing in the corpus renders either, and both are
// rich sources of script-bearing shapes (`<svg onload>`, foreignObject).
//
// `FORBID_TAGS` closes the form family, which the html profile otherwise keeps.
// Nothing renders a form legitimately — the corpus contains zero `<form>`,
// `<input>`, `<button>`, `<select>`, `<textarea>`, `<option>`, `<label>` and
// `<fieldset>` across all 23,528 richtext values — while a published system
// containing a convincing sign-in box that POSTs to somebody else's host is
// credential phishing served from our own origin, wearing our own styling.
// Stripping `javascript:` from `action` (which DOMPurify does) does not address
// that at all, because the phishing form's action is an ordinary https URL.
const CONFIG = {
  USE_PROFILES: { html: true },
  FORBID_TAGS: ['form', 'input', 'button', 'select', 'textarea', 'option', 'label', 'fieldset'],
};

// ── The `style` attribute ──────────────────────────────────────────────────
//
// DOMPurify keeps `style` and does not parse the CSS inside it, which the XSS
// e2e caught: of ten payloads it declawed nine, and left
// `style="background:url('javascript:…')"` intact. That particular string does
// not execute in any current browser, but `style` is not therefore harmless —
// `position:fixed;inset:0;z-index:9999` is a full-page overlay over a published
// system, and `url(https://attacker/)` is a beacon that fires on view.
//
// Dropping `style` outright was the first instinct and would have been a
// regression: 871 values in the corpus carry one, all of it table layout, and
// because the DOM parity gate diffs innerText a purely visual break is exactly
// what it CANNOT see. So the corpus was counted instead. It uses six
// properties, in total, across all 23,528 values:
//
//   white-space 623 · text-align 227 · word-wrap 19 · vertical-align 13 ·
//   padding-left 13 · color 8
//
// Six is small enough that an allowlist is a complete description rather than a
// guess, which is what makes this different from a denylist of dangerous CSS.
// The set below is those six plus their immediate typographic siblings, so an
// author writing ordinary formatting is not surprised. The positioning and
// compositing family — position, top/left/inset, z-index, transform, opacity,
// content — is deliberately absent, because that is the overlay attack, and so
// is anything that can fetch a URL.
const ALLOWED_CSS = new Set([
  'white-space', 'word-wrap', 'overflow-wrap', 'word-break',
  'text-align', 'vertical-align', 'text-decoration', 'text-transform',
  'padding', 'padding-left', 'padding-right', 'padding-top', 'padding-bottom',
  'margin-left', 'margin-right',
  'color', 'background-color',
  'font-style', 'font-weight', 'font-variant', 'line-height',
]);

let hooked = false;
function installHooks() {
  if (hooked) return;
  hooked = true;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    // There was a `rel="noopener noreferrer"` rule here, to stop an authored
    // `target=_blank` link from reaching this page through `window.opener`. It
    // was removed on being measured: the html profile does not allow `target`
    // at all, so the rule could never fire, and a hook that cannot fire is a
    // guard that cannot fail. `sanitizeHtml.test.js` asserts the stripping
    // directly instead, which is the property that actually holds.
    //
    // Filter CSS through the browser's own parser rather than a regex over the
    // attribute text: by the time this hook runs the declarations are already
    // parsed, so shorthand expansion, comments, escapes and casing are the
    // parser's problem and not ours. A property that is not on the list goes,
    // and `background:url(…)` goes with it because `background` is not listed.
    // An image shows only one of the campaign's own uploads (issue 1566): the
    // display or thumbnail WebP under `/uploads/`, on this origin or the API's.
    // Any other address (another host, a data: URI) loses it, so authored
    // HTML can never hot-link a picture or beacon out on view.
    if (node.nodeName === 'IMG') {
      if (!isUploadAddress(node.getAttribute('src'))) node.removeAttribute('src');
      node.removeAttribute('srcset');
    }
    if (node.hasAttribute && node.hasAttribute('style') && node.style) {
      for (let i = node.style.length - 1; i >= 0; i -= 1) {
        const prop = node.style.item(i);
        if (!ALLOWED_CSS.has(prop)) node.style.removeProperty(prop);
      }
      if (!node.style.length) node.removeAttribute('style');
    }
  });
}

/**
 * Sanitise an authored HTML string for `dangerouslySetInnerHTML`.
 *
 * Returns a string in every case: a non-string (a number an author typed into a
 * richtext field, a null from an absent attribute) becomes '' rather than
 * reaching DOMPurify, because `sanitize` coerces its argument and an object
 * with a hostile `toString` is a shape worth not having.
 */
export default function sanitizeHtml(value) {
  if (typeof value !== 'string' || value === '') return '';
  installHooks();
  return DOMPurify.sanitize(value, CONFIG);
}
